/**
 * AlignmentGuides.ts
 * Gère les guides d'alignement intelligents pour faciliter le positionnement des objets
 */
import Konva from 'konva';
interface AlignmentLine {
  type: 'vertical' | 'horizontal';
  value: number;
  distance?: number; // PHASE 3: Distance en pixels pour l'affichage
  label?: string; // PHASE 3: Label à afficher (ex: "50px")
}
interface AlignmentResult {
  x: number;
  y: number;
  alignmentCount: number;
}
export class AlignmentGuides {
  private layer: Konva.Layer;
  private guidesLayer: Konva.Layer;
  private stage: Konva.Stage;
  private enabled: boolean = true;
  private snapTolerance: number = 5;
  private guideLines: Konva.Line[] = [];
  private guideLabels: Konva.Text[] = []; // PHASE 3: Étiquettes de distance
  private snapToGuides: boolean = true;
  private showGuides: boolean = true;

  // OPTIMISATION : Object Pooling pour éviter les allocations mémoire
  private vLinesPool: Konva.Line[] = [];
  private hLinesPool: Konva.Line[] = [];
  private labelPool: Konva.Text[] = [];
  private activeVLines: number = 0;
  private activeHLines: number = 0;
  private activeLabels: number = 0;
  private readonly POOL_SIZE = 10; // Pool de 10 lignes verticales, 10 horizontales, 10 labels

  constructor(stage: Konva.Stage, layer: Konva.Layer) {
    this.stage = stage;
    this.layer = layer;
    // Créer un calque dédié pour les guides (au-dessus du calque principal);
    this.guidesLayer = new Konva.Layer({ listening: false }); // Optim: listening false
    this.stage.add(this.guidesLayer);
    this.guidesLayer.moveToTop();

    // Initialiser le pool de lignes
    this.initPool();
  }

  /**
   * Initialise le pool de lignes guides (réutilisation d'objets)
   */
  private initPool(): void {
    const lineStyle = {
      stroke: '#ff0000',
      strokeWidth: 2,
      dash: [8, 4],
      listening: false,
      opacity: 0.8,
      visible: false, // Cachées par défaut
      name: 'alignmentGuide',
    };

    const labelStyle = {
      fontSize: 12,
      fontFamily: 'Arial',
      fill: '#ff0000',
      padding: 4,
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      listening: false,
      name: 'alignmentGuideLabel',
      visible: false,
    };

    // Créer le pool de lignes verticales
    for (let i = 0; i < this.POOL_SIZE; i++) {
      const vLine = new Konva.Line({ points: [0, 0, 0, 0], ...lineStyle });
      const hLine = new Konva.Line({ points: [0, 0, 0, 0], ...lineStyle });
      const label = new Konva.Text({ x: 0, y: 0, text: '', ...labelStyle });

      this.vLinesPool.push(vLine);
      this.hLinesPool.push(hLine);
      this.labelPool.push(label);

      this.guidesLayer.add(vLine);
      this.guidesLayer.add(hLine);
      this.guidesLayer.add(label);
    }
  }
  /**
   * Active ou désactive les guides d'alignement;
   */
  public setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) {
      this.clearGuides();
    }
  }
  /**
   * Vérifie si les guides sont activés;
   */
  public isEnabled(): boolean {
    return this.enabled;
  }
  /**
   * Vérifie si le snap à la grille est activé
   */
  public isSnapToGridEnabled(): boolean {
    return this.snapToGuides;
  }
  /**
   * OPTIMISATION : Réutilise une ligne verticale du pool
   */
  private drawVerticalGuide(x: number): void {
    if (this.activeVLines >= this.vLinesPool.length) {
      return; // Pool épuisé (ne devrait jamais arriver avec POOL_SIZE=10)
    }
    const line = this.vLinesPool[this.activeVLines++];
    const stageHeight = this.stage.height();
    line.points([x, 0, x, stageHeight]);
    line.visible(true);
  }

  /**
   * OPTIMISATION : Réutilise une ligne horizontale du pool
   */
  private drawHorizontalGuide(y: number): void {
    if (this.activeHLines >= this.hLinesPool.length) {
      return; // Pool épuisé
    }
    const line = this.hLinesPool[this.activeHLines++];
    const stageWidth = this.stage.width();
    line.points([0, y, stageWidth, y]);
    line.visible(true);
  }

  /**
   * OPTIMISATION : Réutilise un label du pool
   */
  private drawLabel(x: number, y: number, text: string): void {
    if (this.activeLabels >= this.labelPool.length) {
      return; // Pool épuisé
    }
    const label = this.labelPool[this.activeLabels++];
    label.x(x);
    label.y(y);
    label.text(text);
    label.visible(true);
  }

  /**
   * Dessine les lignes guides d'alignement avec étiquettes de distance
   * OPTIMISATION : Utilise l'object pooling au lieu de créer/détruire
   */
  private drawGuides(alignments: AlignmentLine[]): void {
    this.clearGuides(); // Reset du pool (cache les lignes)
    const _stageWidth = this.stage.width();
    const _stageHeight = this.stage.height();

    alignments.forEach(alignment => {
      if (alignment.type === 'vertical') {
        this.drawVerticalGuide(alignment.value);
      } else {
        this.drawHorizontalGuide(alignment.value);
      }

      // Ajouter une étiquette de distance si disponible
      if (alignment.label && alignment.distance !== undefined) {
        const labelX = alignment.type === 'vertical' ? alignment.value + 8 : 8;
        const labelY = alignment.type === 'vertical' ? 8 : alignment.value + 8;
        this.drawLabel(labelX, labelY, alignment.label);
      }
    });

    this.guidesLayer.batchDraw();
  }
  /**
   * Efface tous les guides d'alignement
   * OPTIMISATION : Cache les lignes au lieu de les détruire (object pooling)
   */
  public clearGuides(): void {
    // Cacher toutes les lignes du pool au lieu de les détruire
    this.vLinesPool.forEach(line => line.visible(false));
    this.hLinesPool.forEach(line => line.visible(false));
    this.labelPool.forEach(label => label.visible(false));

    // Reset des compteurs
    this.activeVLines = 0;
    this.activeHLines = 0;
    this.activeLabels = 0;

    // Nettoyer les anciennes références (pour compatibilité)
    this.guideLines = [];
    this.guideLabels = [];

    // Pas besoin de batchDraw si on cache juste (gain de performance)
  }
  /**
   * Active ou désactive le snap aux guides;
   */
  public setSnapToGuides(enabled: boolean): void {
    this.snapToGuides = enabled;
  }
  /**
   * Active ou désactive l'affichage des guides;
   */
  public setShowGuides(show: boolean): void {
    this.showGuides = show;
    if (!show) {
      this.clearGuides();
    }
  }
  /**
   * Définit la tolérance de snap;
   */
  public setSnapTolerance(tolerance: number): void {
    this.snapTolerance = Math.max(1, Math.min(20, tolerance));
  }
  /**
   * Obtient la tolérance de snap actuelle;
   */
  public getSnapTolerance(): number {
    return this.snapTolerance;
  }
  /**
   * Vérifie si le snap aux guides est activé
   */
  public isSnapToGuidesEnabled(): boolean {
    return this.snapToGuides;
  }
  /**
   * Vérifie si l'affichage des guides est activé
   */
  public isShowGuidesEnabled(): boolean {
    return this.showGuides;
  }
  /**
   * Détecte les alignements avec le centre du stage;
   */
  private checkStageCenterAlignment(
    movingNode: Konva.Node,
    drawGuides: boolean = false
  ): AlignmentResult {
    const nodeBox = movingNode.getClientRect();
    const nodeCenterX = nodeBox.x + nodeBox.width / 2;
    const nodeCenterY = nodeBox.y + nodeBox.height / 2;
    const stageCenterX = this.stage.width() / 2;
    const stageCenterY = this.stage.height() / 2;
    let snappedX = movingNode.x();
    let snappedY = movingNode.y();
    const alignments: AlignmentLine[] = [];
    // Alignement avec le centre horizontal du stage;
    if (Math.abs(nodeCenterX - stageCenterX) < this.snapTolerance) {
      alignments.push({ type: 'vertical', value: stageCenterX });
      snappedX = movingNode.x() + (stageCenterX - nodeCenterX);
    }
    // Alignement avec le centre vertical du stage;
    if (Math.abs(nodeCenterY - stageCenterY) < this.snapTolerance) {
      alignments.push({ type: 'horizontal', value: stageCenterY });
      snappedY = movingNode.y() + (stageCenterY - nodeCenterY);
    }
    if (drawGuides && this.showGuides && alignments.length > 0) {
      this.drawGuides(alignments);
    }
    return { x: snappedX, y: snappedY, alignmentCount: alignments.length };
  }
  /**
   * Détecte les alignements avec les bords du stage;
   */
  private checkStageEdgeAlignment(
    movingNode: Konva.Node,
    drawGuides: boolean = false
  ): AlignmentResult {
    const nodeBox = movingNode.getClientRect();
    const nodeLeft = nodeBox.x;
    const nodeRight = nodeBox.x + nodeBox.width;
    const nodeTop = nodeBox.y;
    const nodeBottom = nodeBox.y + nodeBox.height;
    let snappedX = movingNode.x();
    let snappedY = movingNode.y();
    const alignments: AlignmentLine[] = [];
    // Alignement avec les bords du stage;
    if (Math.abs(nodeLeft) < this.snapTolerance) {
      alignments.push({ type: 'vertical', value: 0 });
      snappedX = 0;
    }
    if (Math.abs(nodeRight - this.stage.width()) < this.snapTolerance) {
      alignments.push({ type: 'vertical', value: this.stage.width() });
      snappedX = this.stage.width() - nodeBox.width;
    }
    if (Math.abs(nodeTop) < this.snapTolerance) {
      alignments.push({ type: 'horizontal', value: 0 });
      snappedY = 0;
    }
    if (Math.abs(nodeBottom - this.stage.height()) < this.snapTolerance) {
      alignments.push({ type: 'horizontal', value: this.stage.height() });
      snappedY = this.stage.height() - nodeBox.height;
    }
    if (drawGuides && this.showGuides && alignments.length > 0) {
      this.drawGuides(alignments);
    }
    return { x: snappedX, y: snappedY, alignmentCount: alignments.length };
  }
  /**
   * Détecte les alignements avec les objets (méthode améliorée);
   */
  private checkObjectAlignment(
    movingNode: Konva.Node,
    drawGuides: boolean = false
  ): AlignmentResult {
    if (!this.enabled || !this.snapToGuides) {
      return { x: movingNode.x(), y: movingNode.y(), alignmentCount: 0 } as AlignmentResult;
    }
    const nodeBox = movingNode.getClientRect();
    const nodeCenterX = nodeBox.x + nodeBox.width / 2;
    const nodeCenterY = nodeBox.y + nodeBox.height / 2;
    const nodeLeft = nodeBox.x;
    const nodeRight = nodeBox.x + nodeBox.width;
    const nodeTop = nodeBox.y;
    const nodeBottom = nodeBox.y + nodeBox.height;
    const alignments: AlignmentLine[] = [];
    let snappedX = movingNode.x();
    let snappedY = movingNode.y();
    // Parcourir tous les autres objets du calque;
    const objects = this.layer.children.filter(child => {
      return (
        child !== movingNode &&
        child.getClassName() !== 'Transformer' &&
        child.name() !== 'background' &&
        child.name() !== 'backgroundImage' &&
        child.visible()
      );
    });
    if (objects.length === 0) {
      return { x: snappedX, y: snappedY, alignmentCount: 0 } as AlignmentResult;
    }
    // Vérifier l'alignement avec chaque objet;
    objects.forEach(object => {
      const objectBox = object.getClientRect();
      const objectCenterX = objectBox.x + objectBox.width / 2;
      const objectCenterY = objectBox.y + objectBox.height / 2;
      const objectLeft = objectBox.x;
      const objectRight = objectBox.x + objectBox.width;
      const objectTop = objectBox.y;
      const objectBottom = objectBox.y + objectBox.height;
      // Alignements verticaux avec calcul de distance;
      if (Math.abs(nodeCenterX - objectCenterX) < this.snapTolerance) {
        const distance = Math.abs(nodeCenterX - objectCenterX);
        alignments.push({
          type: 'vertical',
          value: objectCenterX,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedX = movingNode.x() + (objectCenterX - nodeCenterX);
      } else if (Math.abs(nodeLeft - objectLeft) < this.snapTolerance) {
        const distance = Math.abs(nodeLeft - objectLeft);
        alignments.push({
          type: 'vertical',
          value: objectLeft,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedX = movingNode.x() + (objectLeft - nodeLeft);
      } else if (Math.abs(nodeRight - objectRight) < this.snapTolerance) {
        const distance = Math.abs(nodeRight - objectRight);
        alignments.push({
          type: 'vertical',
          value: objectRight,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedX = movingNode.x() + (objectRight - nodeRight);
      } else if (Math.abs(nodeLeft - objectRight) < this.snapTolerance) {
        const distance = Math.abs(nodeLeft - objectRight);
        alignments.push({
          type: 'vertical',
          value: objectRight,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedX = movingNode.x() + (objectRight - nodeLeft);
      } else if (Math.abs(nodeRight - objectLeft) < this.snapTolerance) {
        const distance = Math.abs(nodeRight - objectLeft);
        alignments.push({
          type: 'vertical',
          value: objectLeft,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedX = movingNode.x() + (objectLeft - nodeRight);
      }
      // Alignements horizontaux avec calcul de distance;
      if (Math.abs(nodeCenterY - objectCenterY) < this.snapTolerance) {
        const distance = Math.abs(nodeCenterY - objectCenterY);
        alignments.push({
          type: 'horizontal',
          value: objectCenterY,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedY = movingNode.y() + (objectCenterY - nodeCenterY);
      } else if (Math.abs(nodeTop - objectTop) < this.snapTolerance) {
        const distance = Math.abs(nodeTop - objectTop);
        alignments.push({
          type: 'horizontal',
          value: objectTop,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedY = movingNode.y() + (objectTop - nodeTop);
      } else if (Math.abs(nodeBottom - objectBottom) < this.snapTolerance) {
        const distance = Math.abs(nodeBottom - objectBottom);
        alignments.push({
          type: 'horizontal',
          value: objectBottom,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedY = movingNode.y() + (objectBottom - nodeBottom);
      } else if (Math.abs(nodeTop - objectBottom) < this.snapTolerance) {
        const distance = Math.abs(nodeTop - objectBottom);
        alignments.push({
          type: 'horizontal',
          value: objectBottom,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedY = movingNode.y() + (objectBottom - nodeTop);
      } else if (Math.abs(nodeBottom - objectTop) < this.snapTolerance) {
        const distance = Math.abs(nodeBottom - objectTop);
        alignments.push({
          type: 'horizontal',
          value: objectTop,
          distance: Math.round(distance),
          label: distance < 1 ? 'aligné' : `${Math.round(distance)}px`,
        });
        snappedY = movingNode.y() + (objectTop - nodeBottom);
      }
    });
    if (drawGuides && this.showGuides && alignments.length > 0) {
      this.drawGuides(alignments);
    }
    return { x: snappedX, y: snappedY, alignmentCount: alignments.length };
  }
  /**
   * PHASE 3: Vérifie l'alignement pendant la transformation (redimensionnement)
   * Utilisé pour le snapping lors du redimensionnement avec le Transformer
   */
  public checkAlignmentDuringTransform(
    node: Konva.Node,
    newWidth: number,
    newHeight: number,
    newX: number,
    newY: number
  ): { x: number; y: number; width: number; height: number } {
    if (!this.enabled || !this.snapToGuides) {
      return { x: newX, y: newY, width: newWidth, height: newHeight };
    }

    // Créer un nœud temporaire pour calculer les alignements
    const tempNode = node.clone();
    tempNode.x(newX);
    tempNode.y(newY);
    tempNode.width(newWidth);
    tempNode.height(newHeight);
    tempNode.scaleX(1);
    tempNode.scaleY(1);

    const alignedPos = this.checkAlignment(tempNode);
    tempNode.destroy();

    return {
      x: alignedPos.x,
      y: alignedPos.y,
      width: newWidth,
      height: newHeight,
    };
  }

  /**
   * PHASE 3: Vérifie le snapping de rotation (45° ou 90°)
   */
  public checkRotationSnap(
    currentRotation: number,
    snapAngles: number[] = [0, 45, 90, 135, 180, 225, 270, 315]
  ): number {
    if (!this.enabled || !this.snapToGuides) {
      return currentRotation;
    }

    const tolerance = 5; // Degrés de tolérance
    for (const snapAngle of snapAngles) {
      const diff = Math.abs(currentRotation - snapAngle);
      if (diff < tolerance || diff > 360 - tolerance) {
        return snapAngle;
      }
    }
    return currentRotation;
  }

  /**
   * Méthode principale de détection d'alignement (améliorée);
   */
  public checkAlignment(movingNode: Konva.Node): { x: number; y: number } {
    if (!this.enabled) {
      return { x: movingNode.x(), y: movingNode.y() };
    }
    // Vérifier d'abord les alignements avec le stage (sans dessiner les guides);
    const stageCenterResult = this.checkStageCenterAlignment(movingNode, false);
    const stageEdgeResult = this.checkStageEdgeAlignment(movingNode, false);
    // Puis vérifier les alignements avec les objets (sans dessiner les guides);
    const objectResult = this.checkObjectAlignment(movingNode, false);
    // Retourner le résultat qui a le plus d'alignements détectés;
    const results = [
      { result: stageCenterResult, type: 'center' as const },
      { result: stageEdgeResult, type: 'edge' as const },
      { result: objectResult, type: 'object' as const },
    ];
    const bestEntry = results.reduce((best, current) =>
      current.result.alignmentCount > best.result.alignmentCount ? current : best
    );
    // Dessiner les guides si nécessaire et récupérer le résultat final;
    let finalResult = bestEntry.result;
    if (this.showGuides && bestEntry.result.alignmentCount > 0) {
      this.clearGuides();
      // Recalculer et dessiner les guides pour le meilleur résultat;
      // Utiliser le type pour identifier quelle méthode appeler;
      if (bestEntry.type === 'center') {
        finalResult = this.checkStageCenterAlignment(movingNode, true);
      } else if (bestEntry.type === 'edge') {
        finalResult = this.checkStageEdgeAlignment(movingNode, true);
      } else {
        finalResult = this.checkObjectAlignment(movingNode, true);
      }
    }
    return { x: finalResult.x, y: finalResult.y };
  }
  /**
   * Détruit le calque des guides et nettoie le pool
   */
  public destroy(): void {
    this.clearGuides();
    // Détruire les objets du pool
    this.vLinesPool.forEach(line => line.destroy());
    this.hLinesPool.forEach(line => line.destroy());
    this.labelPool.forEach(label => label.destroy());
    this.vLinesPool = [];
    this.hLinesPool = [];
    this.labelPool = [];
    this.guidesLayer.destroy();
  }
}
